import { useEffect, useMemo, useRef, useState } from 'react'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { Column } from './Column'
import { CardTile } from './CardTile'
import { COLUMNS, STATUSES, type Assignee, type Card, type Status } from '../lib/types'
import type { Columns } from '../lib/useBoard'

const COLUMN_PREFIX = 'column:'

type Props = {
  columns: Columns
  filter: Assignee | 'all'
  defaultAssignee: Assignee
  onAdd: (input: { title: string; assignee: Assignee; status: Status }) => void
  onOpen: (card: Card) => void
  onToggleDone: (card: Card) => void
  onMove: (cardId: string, columns: Columns, status: Status, index: number) => void
}

/** Which column a droppable id sits in — either a column itself or a card within one. */
function columnOf(columns: Columns, id: string): Status | null {
  if (id.startsWith(COLUMN_PREFIX)) {
    const status = id.slice(COLUMN_PREFIX.length) as Status
    return STATUSES.includes(status) ? status : null
  }
  return STATUSES.find((s) => columns[s].some((c) => c.id === id)) ?? null
}

/**
 * Translate a move made in the (possibly filtered) view back onto the full board.
 * The card is placed relative to whichever neighbour it landed next to on screen,
 * so filtering by assignee never scrambles the hidden cards' order.
 */
function applyToFullBoard(
  full: Columns,
  view: Columns,
  cardId: string,
  destination: Status,
): { columns: Columns; index: number } | null {
  const source = STATUSES.find((s) => full[s].some((c) => c.id === cardId))
  const card = source ? full[source].find((c) => c.id === cardId) : undefined
  if (!card) return null

  const next: Columns = Object.fromEntries(
    STATUSES.map((s) => [s, full[s].filter((c) => c.id !== cardId)]),
  ) as Columns

  const viewOrder = view[destination]
  const viewIndex = viewOrder.findIndex((c) => c.id === cardId)
  const previous = viewIndex > 0 ? viewOrder[viewIndex - 1] : undefined
  const following = viewIndex >= 0 ? viewOrder[viewIndex + 1] : undefined

  const target = next[destination]
  let index = target.length
  if (previous) {
    const at = target.findIndex((c) => c.id === previous.id)
    if (at >= 0) index = at + 1
  } else if (following) {
    const at = target.findIndex((c) => c.id === following.id)
    if (at >= 0) index = at
  } else if (viewIndex === 0) {
    // Dropped into a column whose visible cards are all hidden by the filter.
    index = 0
  }

  target.splice(index, 0, { ...card, status: destination })
  return { columns: next, index }
}

/**
 * Where the pointer actually finished, resolved straight off the DOM.
 *
 * dnd-kit needs a render cycle after the drag activates before it can compute
 * collisions. When the browser coalesces pointer events — a flicked drag, a busy
 * main thread — that cycle never happens and `over` still points at the dragged
 * card, which reads as "nothing moved". Geometry doesn't have that problem.
 */
function dropTargetAtPoint(
  x: number,
  y: number,
  activeId: string,
): { status: Status; overId: string } | null {
  const columns = [...document.querySelectorAll<HTMLElement>('[data-column]')]
  if (columns.length === 0) return null

  // Columns sit side by side, so horizontal distance alone identifies one.
  let best: { el: HTMLElement; distance: number } | null = null
  for (const el of columns) {
    const rect = el.getBoundingClientRect()
    const distance = x < rect.left ? rect.left - x : x > rect.right ? x - rect.right : 0
    if (!best || distance < best.distance) best = { el, distance }
  }
  if (!best) return null

  const status = best.el.dataset.column as Status | undefined
  if (!status || !STATUSES.includes(status)) return null

  // Drop above the first card whose midpoint sits below the pointer.
  const cards = [...best.el.querySelectorAll<HTMLElement>('[data-card-id]')].filter(
    (el) => el.dataset.cardId !== activeId,
  )
  for (const el of cards) {
    const rect = el.getBoundingClientRect()
    if (y < rect.top + rect.height / 2) {
      return { status, overId: el.dataset.cardId! }
    }
  }
  return { status, overId: `${COLUMN_PREFIX}${status}` }
}

export function Board({
  columns,
  filter,
  defaultAssignee,
  onAdd,
  onOpen,
  onToggleDone,
  onMove,
}: Props) {
  const [activeCard, setActiveCard] = useState<Card | null>(null)
  // While a drag is in flight the view is owned locally so cards can slide about
  // without every pointer move hitting the server. The ref mirrors it so the
  // drop handler never reads a board React hasn't committed yet.
  const [dragView, setDragView] = useState<Columns | null>(null)
  const dragViewRef = useRef<Columns | null>(null)

  // The browser's own last pointer position. dnd-kit's `delta` is measured from
  // the moment the drag activates, so on a single large jump it is ~0 and points
  // back at the card's origin. This is the real drop location.
  const pointerRef = useRef<{ x: number; y: number } | null>(null)
  useEffect(() => {
    const track = (e: PointerEvent) => {
      pointerRef.current = { x: e.clientX, y: e.clientY }
    }
    const opts = { capture: true, passive: true } as const
    for (const type of ['pointerdown', 'pointermove', 'pointerup'] as const) {
      window.addEventListener(type, track, opts)
    }
    return () => {
      for (const type of ['pointerdown', 'pointermove', 'pointerup'] as const) {
        window.removeEventListener(type, track, opts)
      }
    }
  }, [])

  const filteredColumns = useMemo<Columns>(
    () =>
      Object.fromEntries(
        STATUSES.map((s) => [
          s,
          filter === 'all' ? columns[s] : columns[s].filter((c) => c.assignee === filter),
        ]),
      ) as Columns,
    [columns, filter],
  )

  const view = dragView ?? filteredColumns

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  /** Move `activeId` into `to` at the index it visually landed on. */
  function stageMove(base: Columns, activeId: string, to: Status, overId: string): Columns {
    const from = STATUSES.find((s) => base[s].some((c) => c.id === activeId))
    if (!from || from === to) return base
    const card = base[from].find((c) => c.id === activeId)
    if (!card) return base

    const destination = base[to]
    const overIndex = destination.findIndex((c) => c.id === overId)
    const insertAt = overIndex >= 0 ? overIndex : destination.length

    return {
      ...base,
      [from]: base[from].filter((c) => c.id !== activeId),
      [to]: [
        ...destination.slice(0, insertAt),
        { ...card, status: to },
        ...destination.slice(insertAt),
      ],
    }
  }

  function handleDragStart({ active }: DragStartEvent) {
    const status = columnOf(view, String(active.id))
    setActiveCard(status ? (view[status].find((c) => c.id === active.id) ?? null) : null)
    dragViewRef.current = filteredColumns
    setDragView(filteredColumns)
  }

  function handleDragOver({ active, over }: DragOverEvent) {
    if (!over) return
    const activeId = String(active.id)
    const base = dragViewRef.current ?? filteredColumns
    const from = columnOf(base, activeId)
    const to = columnOf(base, String(over.id))
    if (!from || !to || from === to) return

    const next = stageMove(base, activeId, to, String(over.id))
    dragViewRef.current = next
    setDragView(next)
  }

  function handleDragEnd({ active, over, delta, activatorEvent }: DragEndEvent) {
    const activeId = String(active.id)
    // Read through the ref, not state: on a fast drag the last onDragOver may
    // not have committed yet, and a stale board here reads as "nothing moved".
    let base = dragViewRef.current ?? filteredColumns
    setActiveCard(null)
    setDragView(null)
    dragViewRef.current = null

    let overId = over ? String(over.id) : null

    // Once onDragOver has staged a cross-column move, the card occupies the slot
    // it was dropped into, so `over` settling on the dragged card itself means
    // "already in the right place". Only reach for geometry when dnd-kit gave us
    // nothing usable at all.
    const stagedIn = STATUSES.find((s) => base[s].some((c) => c.id === activeId))
    const startedIn = STATUSES.find((s) => columns[s].some((c) => c.id === activeId))
    const alreadyStaged = Boolean(stagedIn && startedIn && stagedIn !== startedIn)

    if (!overId || (overId === activeId && !alreadyStaged)) {
      const start = activatorEvent as PointerEvent | MouseEvent | null
      const point =
        pointerRef.current ??
        (start && 'clientX' in start ? { x: start.clientX + delta.x, y: start.clientY + delta.y } : null)
      const fallback = point ? dropTargetAtPoint(point.x, point.y, activeId) : null
      if (!fallback) return
      overId = fallback.overId
    }

    // Destination comes from what the pointer is actually over. Deriving it from
    // where the card sits in drag state made the drop depend on onDragOver
    // having already run, so a quick flick silently did nothing.
    const destination = columnOf(base, overId)
    if (!destination) return

    // Idempotent: if onDragOver never staged this cross-column move, do it now.
    base = stageMove(base, activeId, destination, overId)

    let settled = base
    if (overId !== activeId && !overId.startsWith(COLUMN_PREFIX)) {
      const list = base[destination]
      const fromIndex = list.findIndex((c) => c.id === activeId)
      const toIndex = list.findIndex((c) => c.id === overId)
      if (fromIndex >= 0 && toIndex >= 0 && fromIndex !== toIndex) {
        settled = { ...base, [destination]: arrayMove(list, fromIndex, toIndex) }
      }
    }

    const result = applyToFullBoard(columns, settled, activeId, destination)
    if (!result) return

    const original = STATUSES.find((s) => columns[s].some((c) => c.id === activeId))
    const originalIndex = original ? columns[original].findIndex((c) => c.id === activeId) : -1
    if (original === destination && originalIndex === result.index) return

    onMove(activeId, result.columns, destination, result.index)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={() => {
        setActiveCard(null)
        setDragView(null)
        dragViewRef.current = null
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {COLUMNS.map((column) => (
          <Column
            key={column.id}
            status={column.id}
            label={column.label}
            accent={column.accent}
            cards={view[column.id]}
            filtered={filter !== 'all'}
            defaultAssignee={defaultAssignee}
            onAdd={onAdd}
            onOpen={onOpen}
            onToggleDone={onToggleDone}
          />
        ))}
      </div>

      <DragOverlay dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.2, 0, 0, 1)' }}>
        {activeCard && (
          <CardTile card={activeCard} onOpen={() => {}} onToggleDone={() => {}} overlay />
        )}
      </DragOverlay>
    </DndContext>
  )
}
